import * as THREE from 'three';
import { makeNebulaTag, paintNebulaTag } from './nebulaTag';
import { placeNebulaTags } from './placeTags';
import { rimLocal, titleContentRadius } from './tagLayout';
import { CLOUD_PLANE, MAX_CLOUD_PTS, SMOKE_LAYERS, makeSmokeMaterial } from './smokeMaterial';

const MAX_NEBULA_BLOBS = 10;
const _tagWorld = new THREE.Vector3();

function disposeBunch(bunch) {
  const tag = bunch.userData.tag;
  if (tag) {
    bunch.remove(tag);
    tag.element.remove();
    bunch.userData.tag = null;
  }
  bunch.children.forEach((mesh) => mesh.material?.dispose?.());
}

function makeCloudBunch(tint) {
  const bunch = new THREE.Group();
  SMOKE_LAYERS.forEach((layer) => {
    const mesh = new THREE.Mesh(CLOUD_PLANE, makeSmokeMaterial(tint, layer));
    mesh.userData.sharedGeom = true;
    mesh.userData.layer = layer;
    mesh.renderOrder = layer.order;
    mesh.frustumCulled = true;
    bunch.add(mesh);
  });
  return bunch;
}

function placeCloud(bunch, cluster) {
  const pts = cluster.points || [];
  let span = 0;
  pts.forEach(([px, py]) => {
    span = Math.max(span, Math.hypot(px - cluster.cx, py - cluster.cy));
  });
  const maxR = Math.max(span, 0.35);
  const content = titleContentRadius(pts, cluster.cx, cluster.cy);
  const stretch = cluster.stretch ?? 1.12;
  const pad = 16;
  bunch.position.set(cluster.cx, cluster.cy, 0);
  bunch.userData.clusterKey = cluster.key;
  bunch.userData.tint = cluster.tint;
  bunch.userData.timeMul = cluster.timeMul ?? 1;
  bunch.userData.timeOff = cluster.timeOff ?? 0;
  const z0 = cluster.z ?? 0;
  const zBack = cluster.hasNests ? (cluster.nestZ ?? z0) - 1.3 : z0 + (SMOKE_LAYERS[0].zOff);
  const axis = cluster.axis || [1, 0];
  const seed = cluster.seed || [0, 0];
  let tag = bunch.userData.tag;
  if (!tag) {
    tag = makeNebulaTag(cluster, bunch.parent?.userData.renameTagRef);
    bunch.add(tag);
    bunch.userData.tag = tag;
  } else {
    paintNebulaTag(tag.element, cluster);
  }
  bunch.userData.halfW = cluster.halfW || maxR;
  bunch.userData.halfH = cluster.halfH || maxR;
  bunch.userData.contentW = content;
  bunch.userData.contentH = content;
  bunch.userData.tagRimK = Math.min(bunch.userData.tagRimK ?? 0.56, 0.7);
  bunch.userData.tagZ = (cluster.z ?? 0) + 0.45;
  bunch.userData.tagAngle = bunch.userData.tagAngle ?? Math.PI / 2;
  const rim = rimLocal(bunch, bunch.userData.tagAngle);
  tag.position.set(rim.x, rim.y, rim.z);

  bunch.children.forEach((mesh) => {
    const layer = mesh.userData.layer;
    if (!layer) return;
    const nestLayer = !!layer.nestOnly;
    mesh.visible = !nestLayer || !!cluster.hasNests;
    const half = (maxR + pad) * layer.sizeMul;
    const z = cluster.hasNests
      ? z0 + (zBack - z0) * (layer.zT ?? 0)
      : z0 + (layer.zOff ?? -1.8);
    mesh.position.set(0, 0, z);
    mesh.scale.set(half, half, 1);
    const u = mesh.material.uniforms;
    u.uTint.value.set(cluster.tint);
    u.uCount.value = Math.min(pts.length, MAX_CLOUD_PTS);
    u.uRadius.value = layer.radius;
    u.uStretch.value = stretch;
    u.uAxis.value.set(axis[0], axis[1]);
    u.uSeed.value.set(seed[0], seed[1]);
    for (let i = 0; i < MAX_CLOUD_PTS; i += 1) {
      const p = pts[i];
      if (p) u.uPts.value[i].set(p[0], p[1]);
      else u.uPts.value[i].set(0, 0);
    }
  });
}

/**
 * The first clouds: domain-warped gas, three slabs for z-depth, wide feather,
 * slow drift. Float texture + half-float composer so it stays smoke, not dither.
 */
export function createClusterNebula(renameTagRef) {
  const group = new THREE.Group();
  group.userData.isNebula = true;
  group.userData.renameTagRef = renameTagRef;
  group.renderOrder = -30;
  return group;
}

export { projectNebulaTitleBoxes } from './placeTags';

export function syncClusterNebula(group, clusters) {
  const tagged = (clusters || []).filter((c) => c.key !== '__none__');
  const next = new Set(tagged.map((c) => c.key));
  const byKey = new Map();
  group.children.forEach((bunch) => byKey.set(bunch.userData.clusterKey, bunch));

  byKey.forEach((bunch, key) => {
    if (next.has(key)) return;
    group.remove(bunch);
    disposeBunch(bunch);
  });

  tagged.forEach((cluster, i) => {
    let bunch = byKey.get(cluster.key);
    if (!bunch) {
      bunch = makeCloudBunch(cluster.tint);
      group.add(bunch);
    }
    placeCloud(bunch, cluster);
    if (i >= MAX_NEBULA_BLOBS) {
      bunch.children.forEach((mesh) => {
        if (mesh.userData?.layer) mesh.visible = false;
      });
    }
  });
}

export function setNebulaTime(group, t, camera, meshByTaskId, container, edgeLinesByKey, skipPlace = false, occupiedBoxes, idleOrbit = false) {
  group?.children?.forEach((bunch) => {
    const mul = bunch.userData.timeMul ?? 1;
    const off = bunch.userData.timeOff ?? 0;
    bunch.children.forEach((obj) => {
      const u = obj.material?.uniforms;
      if (!u?.uTime) return;
      const scale = obj.userData.layer?.timeScale ?? 1;
      u.uTime.value = (t * mul + off) * scale;
    });
  });
  if (camera && container) {
    placeNebulaTags(
      group,
      camera,
      meshByTaskId,
      container,
      edgeLinesByKey,
      occupiedBoxes,
      skipPlace || idleOrbit,
    );
  }
  group?.children?.forEach((bunch) => {
    const tag = bunch.userData.tag;
    if (!tag?.element || !camera) return;
    if (tag.element.dataset.editing === '1') {
      tag.element.style.opacity = '1';
      tag.element.style.visibility = 'visible';
      return;
    }
    if (tag.element.style.visibility === 'hidden') return;
    tag.getWorldPosition(_tagWorld);
    const dist = camera.position.distanceTo(_tagWorld);
    const vis = THREE.MathUtils.smoothstep(7, 22, dist);
    tag.element.style.opacity = String(0.72 + vis * 0.28);
  });
}
